import React, { useEffect, useState } from "react";
import { Button } from "antd";
import { CheckOutlined, DownOutlined } from "@ant-design/icons";

import { PolicyEstimates, estimateRows } from "../TreatmentPlans/component/shared/EstimateClaimsEditor";
import {
  describeMissingEstimateFields,
  estimateGaps,
  isEstimateComplete,
  isEstimateStarted,
  summariseMissingEstimateFields,
} from "../TreatmentPlans/component/shared/estimateClaimsApi";
import "../TreatmentPlans/component/shared/estimateClaims.css";

/**
 * Step 4 — name the submissions to chase, one policy at a time.
 *
 * Deliberately the same shape as the Policies step before it: one card open at a
 * time, save it, it turns green and the next opens itself. Two consecutive steps
 * that ask "go through these policies and confirm each one" should not ask it in
 * two different visual languages — the user learns the pattern once and re-uses
 * it here.
 *
 * Where it genuinely differs from that step is what saving MEANS. Approving a
 * policy PATCHes it, because the policy already exists on the server. An estimate
 * has nothing to be written to yet — the request that will own it is created on
 * Send — so saving here confirms the details rather than persisting them, and the
 * copy says exactly that instead of implying a write that hasn't happened.
 *
 * Editing a saved policy un-saves it (the host does that in onChange), so a green
 * card always describes what is currently in the form.
 *
 * @param {Array}    policies   the selected policies, in display order
 * @param {Object}   value      host-owned { [policyId]: [estimate, ...] }
 * @param {Object}   saved      host-owned { [policyId]: true }
 * @param {number}   patientId  for the claim-form extract endpoint
 * @param {Function} onChange   (policyId, nextRows) => void
 * @param {Function} onSave     (policyId) => void
 * @param {string}   noun       what one submission is called in the copy —
 *                              "estimate" (default) or "claim" for a claim
 *                              follow-up, which records the same fields.
 */
export default function EstimateChecklist({
  policies,
  value,
  saved,
  patientId,
  providerOptions,
  onChange,
  onSave,
  cdaItrans,
  noun = "estimate",
  nounPlural = noun + "s",
}) {
  const Noun = noun.charAt(0).toUpperCase() + noun.slice(1);
  const list = policies || [];
  const byPolicy = value || {};
  const [expanded, setExpanded] = useState(null);

  function rowsFor(policy) {
    return estimateRows(byPolicy[policy.id], cdaItrans);
  }

  /** Ready when at least one estimate is complete; blank extra rows are dropped on send. */
  function isReady(policy) {
    return rowsFor(policy).some(isEstimateComplete);
  }

  function isSaved(policy) {
    return !!(saved && saved[policy.id]);
  }

  /**
   * Has anything been entered for this policy yet?
   *
   * A form nobody has touched is not a form with mistakes in it. Without this
   * every card opens in the attention colour, listing five things "missing" from
   * a set of fields the user has not been shown yet — which reads as five errors
   * on arrival and drowns out the one thing they should do next.
   */
  function isStarted(policy) {
    return rowsFor(policy).some(isEstimateStarted);
  }

  function carrierOf(policy) {
    return policy.insurance_name_display || policy.carrier_display_name || "this policy";
  }

  /**
   * What to tell them is still missing.
   *
   * With several rows on one policy, describe the one CLOSEST to done — that's the
   * one they are most likely working on, and listing every gap across every row
   * would read as a wall rather than a next action.
   */
  function nearestGap(policy) {
    return rowsFor(policy)
      .map(estimateGaps)
      .sort(function (a, b) { return a.length - b.length; })[0] || [];
  }

  const doneCount = list.filter(isSaved).length;
  const allDone = list.length > 0 && doneCount === list.length;

  // Open whatever still needs the user, and move along as each is saved — but
  // never yank them out of a card they are still working in.
  useEffect(
    function () {
      setExpanded(function (current) {
        if (current !== null) {
          const open = list.find(function (p) { return p.id === current; });
          if (open && !isSaved(open)) return current;
        }
        const nextUp = list.find(function (p) { return !isSaved(p); });
        return nextUp ? nextUp.id : null;
      });
    },
    [doneCount, list.length], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const pct = list.length ? Math.round((doneCount / list.length) * 100) : 0;

  if (!list.length) return null;

  return (
    <div className="ir-check">
      <div className={"ir-check-progress" + (allDone ? " is-done" : "")}>
        <div className="ir-check-progress-row">
          <span className="ir-check-progress-label">
            {allDone
              ? "All " + nounPlural + " recorded"
              : doneCount + " of " + list.length + " recorded"}
          </span>
          <span className="ir-check-progress-sub">
            {allDone
              ? "Ready to send."
              : "Say what to chase on each policy, then save it."}
          </span>
        </div>
        <div className="ir-check-bar">
          <div className="ir-check-bar-fill" style={{ width: pct + "%" }} />
        </div>
      </div>

      {list.map(function (policy, index) {
        const done = isSaved(policy);
        const ready = isReady(policy);
        const started = isStarted(policy);
        const open = expanded === policy.id;
        const count = rowsFor(policy).filter(isEstimateComplete).length;
        const gap = summariseMissingEstimateFields(nearestGap(policy));
        const cls = [
          "ir-check-item",
          open ? "is-open" : "",
          done ? "is-done" : "",
          // Attention is for a form left half-done, never for one not yet begun.
          !done && !ready && started ? "is-gap" : "",
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <div key={policy.id} className={cls}>
            <button
              type="button"
              className="ir-check-head"
              aria-expanded={open}
              onClick={function () { setExpanded(open ? null : policy.id); }}
            >
              <span className="ir-check-badge">
                {done ? <CheckOutlined /> : index + 1}
              </span>
              <span className="ir-check-titles">
                <span className="ir-check-carrier">{carrierOf(policy)}</span>
                <span className="ir-check-state">
                  {done
                    ? count > 1
                      ? count + " " + nounPlural + " saved"
                      : Noun + " saved"
                    : ready
                      ? "Ready to save"
                      : started
                        ? gap + " still needed"
                        : "Nothing recorded yet"}
                </span>
              </span>
              {policy.insurance_type_display ? (
                <span className="ir-check-ordinal">{policy.insurance_type_display}</span>
              ) : null}
              <DownOutlined className="ir-check-chev" />
            </button>

            {open ? (
              <div className="ir-check-body">
                <div className="ir-est-body">
                  <PolicyEstimates
                    patientId={patientId}
                    estimates={byPolicy[policy.id]}
                    providerOptions={providerOptions}
                    cdaItrans={cdaItrans}
                    noun={noun}
                    onChange={function (next) { onChange(policy.id, next); }}
                    // Said in this card's header and beside its Save button
                    // already; a third copy under the fields is what made this
                    // read as a wall of warnings.
                    showGapNote={false}
                    // Inside the drawer, so the calendar must render next to its
                    // input rather than portalling to document.body, where it never
                    // becomes reachable.
                    getPopupContainer={function (trigger) { return trigger.parentElement; }}
                  />
                </div>

                {done ? (
                  <div className="ir-check-done-note">
                    <CheckOutlined /> Saved — edit any field to change it.
                  </div>
                ) : (
                  <div className="ir-check-actions">
                    <span className="ir-check-hint">
                      {ready
                        ? "Saving records this for the request."
                        : nearestGap(policy).length <= 2
                          // Few enough to name: that's a next action, not a list.
                          ? "Add " + describeMissingEstimateFields(nearestGap(policy)) + " to save."
                          : "Upload the claim form above, or fill the fields in, to save."}
                    </span>
                    <Button
                      type="primary"
                      disabled={!ready}
                      onClick={function () { onSave(policy.id); }}
                    >
                      Save
                    </Button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
